import React, { useState, useContext } from "react";
import { Grid, Typography } from "@material-ui/core";
import moment from "moment";
import useStyles from "./styles";
import { translate as t } from "../../../../../infrastructure/translate/localization";
import ModalContext from "../../../../../infrastructure/Contexts/components/ModalWrapper/context";
import RecurrentPaymentContext from "../../../../../infrastructure/Contexts/components/data/RecurrentPaymentsWrapper/context";
import RecurrentPaymentAction from "../../../../../infrastructure/Contexts/components/actions/RecurrentPaymentActionsWrapper/context";
import RowPurchasePrice from "./components/RowPurchasePrice";
import RowPrivateUse from "./components/RowPrivateUse";
import RowEnergeticEfficiency from "./components/RowEnergeticEfficiency";
import RowInitialPrice from "./components/RowInitialPrice";
import RowWhoPaysQuotas from "./components/RowWhoPaysQuotas";
import RowCustomQuota from "./components/RowCustomQuota";
import VatCompanyMessage from "./components/VatCompanyMessage";
import RowDate from "./components/RowDate";
import RowNumberQuotas from "./components/RowNumberQuotas";
import CobeeButton from "../../../CobeeButton";
import RowEmployeeVat from "./components/RowEmployeeVat";

export default function AcceptRentingModal(): JSX.Element {
  const { container, row, buttonRow, fieldRowSpacing } = useStyles();
  const { recurrentPaymentDetail, rentingInitialData } = useContext(
    RecurrentPaymentContext
  );
  const { reviewRentingDetails, reviewRentingDetailsIsLoading } = useContext(
    RecurrentPaymentAction
  );
  const { id } = recurrentPaymentDetail;

  const [privateUsePercentage, setPrivateUsePercentage] = useState(
    rentingInitialData?.privateUsePercentage || 0
  );
  const [energeticEfficiency, setEnergeticEfficiency] = useState(
    rentingInitialData?.energeticEfficiency || 0
  );

  const [purchasePrice, setPurchasePrice] = useState(
    rentingInitialData?.purchasePrice || 0
  );
  const [initialAmount, setInitialAmount] = useState(
    rentingInitialData?.initialAmount || 0
  );

  const [rentingQuota, setRentingQuota] = useState(
    rentingInitialData?.rentingQuota || 0
  );
  const [rentingCompanyQuota, setRentingCompanyQuota] = useState(
    rentingInitialData?.rentingCompanyQuota || 0
  );
  const [rentingEmployeeQuota, setRentingEmployeeQuota] = useState(
    rentingInitialData?.rentingEmployeeQuota || 0
  );

  const [date, setDate] = useState(
    rentingInitialData?.date ||
      moment(new Date()).add(1, "months").startOf("month").toDate()
  );
  const [numberOfQuotas, setNumberOfQuotas] = useState(
    rentingInitialData?.numberOfQuotas || 0
  );

  const [whoPaysQuotas, setWhoPaysQuotas] = useState(
    rentingInitialData?.whoPaysQuotas || "employee"
  );

  const [vatRatioSupportedByEmployee, setVatRatioSupportedByEmployee] =
    useState(rentingInitialData?.vatRatioSupportedByEmployee || 100);

  const { closeModal } = useContext(ModalContext);

  const showAloneRowQuota = () => whoPaysQuotas !== "both";

  const showEmployeeRowVat = () => whoPaysQuotas !== "company";

  const checkRentingQuota = () => {
    if (whoPaysQuotas === "both") {
      return rentingCompanyQuota <= 0 || rentingEmployeeQuota <= 0;
    }
    return rentingQuota <= 0;
  };

  const shouldDisabledResultsButton = () => {
    return numberOfQuotas < 2 || checkRentingQuota() || purchasePrice <= 0;
  };

  return (
    <Grid item xs={12} container justifyContent="center" className={container}>
      <Grid item container xs={12} className={row}>
        <Grid item container xs={12}>
          <Typography variant="h6">
            {t("request-section.information-for-tax-calculation")}
          </Typography>
        </Grid>
        <RowPurchasePrice
          purchasePrice={purchasePrice}
          setPurchasePrice={setPurchasePrice}
        />
        <RowPrivateUse
          privateUsePercentage={privateUsePercentage}
          setPrivateUsePercentage={setPrivateUsePercentage}
        />
        <RowEnergeticEfficiency
          energeticEfficiency={energeticEfficiency}
          setEnergeticEfficiency={setEnergeticEfficiency}
        />
      </Grid>
      <Grid item container xs={12} className={row}>
        <Grid item container xs={12} className={fieldRowSpacing}>
          <Typography variant="h6">
            {t("request-section.collections-and-renting-quotas")}
          </Typography>
        </Grid>
        <RowInitialPrice
          initialAmount={initialAmount}
          setInitialAmount={setInitialAmount}
        />
        <RowWhoPaysQuotas
          whoPaysQuotas={whoPaysQuotas}
          handleChange={setWhoPaysQuotas}
        />
        {showAloneRowQuota() ? (
          <RowCustomQuota
            renting={rentingQuota}
            setRenting={setRentingQuota}
            title={t("request-section.amount-quota")}
            label={t("common-messages.tax-not-included")}
          />
        ) : (
          <>
            <RowCustomQuota
              renting={rentingCompanyQuota}
              setRenting={setRentingCompanyQuota}
              title={t("request-section.amount-quota-paid-company")}
              label={t("common-messages.tax-not-included")}
            />
            <RowCustomQuota
              renting={rentingEmployeeQuota}
              setRenting={setRentingEmployeeQuota}
              title={t("request-section.amount-quota-paid-employee")}
              label={t("common-messages.tax-not-included")}
            />
          </>
        )}
        {showEmployeeRowVat() ? (
          <RowEmployeeVat
            initialValue={vatRatioSupportedByEmployee}
            handleChange={setVatRatioSupportedByEmployee}
          />
        ) : (
          <VatCompanyMessage />
        )}
        <RowDate date={date} setDate={setDate} />
        <RowNumberQuotas
          numberOfQuotas={numberOfQuotas}
          setNumberOfQuotas={setNumberOfQuotas}
        />
      </Grid>
      <Grid
        item
        container
        xs={12}
        justifyContent="center"
        alignItems="center"
        className={buttonRow}
      >
        <CobeeButton styleType="secondary" onClick={closeModal}>
          {t("actions.cancel")}
        </CobeeButton>
        <CobeeButton
          styleType="primary"
          disabled={shouldDisabledResultsButton()}
          isLoading={reviewRentingDetailsIsLoading}
          onClick={() =>
            reviewRentingDetails({
              requestId: id,
              rentingData: {
                privateUsePercentage,
                energeticEfficiency,
                purchasePrice,
                initialAmount,
                rentingQuota,
                rentingEmployeeQuota,
                rentingCompanyQuota,
                date,
                numberOfQuotas,
                whoPaysQuotas,
                vatRatioSupportedByEmployee,
              },
            })
          }
        >
          {t("actions.watch-results")}
        </CobeeButton>
      </Grid>
    </Grid>
  );
}
